import { useState, useEffect, useCallback } from 'react';
import { API } from '@/utils/API';

interface BannerMessageResponse {
  message: string | null;
}

export const useBannerMessage = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBannerMessage = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch('/user_banner_messages');

      if (!response.ok) {
        throw new Error('Failed to fetch banner message');
      }

      const data: BannerMessageResponse = await response.json();
      setMessage(data.message);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load banner message'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const dismissBanner = useCallback(async () => {
    try {
      const response = await API.authenticated.fetch('/user_banner_messages', {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to dismiss banner message');
      }

      // Clear the message after successful dismissal
      setMessage(null);
    } catch (err) {
      console.error('Error dismissing banner:', err);
      // Don't set error state for dismiss failures, just log it
    }
  }, []);

  // Fetch banner message on mount
  useEffect(() => {
    fetchBannerMessage();
  }, [fetchBannerMessage]);

  return {
    message,
    isLoading,
    error,
    dismissBanner,
    refetch: fetchBannerMessage,
  };
};
